import { createContext, type ReactNode, useContext, useMemo } from "react";

/**
 * QL-16 — which surface a component renders on: the advisor flow or the
 * client public flow at `/p/session/...`, plus the client session token there.
 *
 * Kept apart from `PublicFlowProvider` on purpose: that provider's state holds
 * `formValues`, which change on every keystroke. This value only changes when
 * the token does, so readers don't re-render while the client types.
 *
 * Without a provider the value is `{ isPublic: false }` (advisor).
 */
export type PublicSession =
  | { isPublic: true; sessionToken: string }
  | { isPublic: false; sessionToken?: undefined };

const ADVISOR_SESSION: PublicSession = { isPublic: false };

const PublicSessionContext = createContext<PublicSession>(ADVISOR_SESSION);

/** Mounted on the public session page, above `PublicFlowProvider`. */
export function PublicSessionProvider({
  sessionToken,
  children,
}: {
  sessionToken: string;
  children: ReactNode;
}) {
  const value = useMemo<PublicSession>(() => ({ isPublic: true, sessionToken }), [sessionToken]);
  return <PublicSessionContext.Provider value={value}>{children}</PublicSessionContext.Provider>;
}

/** Never throws — returns `{ isPublic: false }` outside the public flow. */
export function usePublicSession(): PublicSession {
  return useContext(PublicSessionContext);
}

/** Public-only: the client session token. Throws outside `PublicSessionProvider`. */
export function useSessionToken(): string {
  const session = useContext(PublicSessionContext);
  if (!session.isPublic) {
    throw new Error("useSessionToken must be used within PublicSessionProvider");
  }
  return session.sessionToken;
}
